/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import {
  orderBy,
  collection,
  doc,
  query,
  where,
  getDocs,
  onSnapshot,
  serverTimestamp,
  runTransaction,
  increment,
} from "firebase/firestore";
import { toast } from "react-hot-toast";
import { db, db_modular } from "../../firebaseConfig";
import React, { useState, useEffect } from "react";
import { calculateHash } from "../../utils/helpers";
import dayjs from "dayjs";
import { UserAuth } from "../../context/authContext";
import { FiSearch, FiFilter, FiBox, FiCpu } from "react-icons/fi";
import { clsx } from "clsx";
import BlockTransactionsModal from "../BlockTransactionsModal";

export default function HackBlocks() {
  const [roomId, setId] = useState(localStorage.getItem("roomId") ?? "");
  const [team, setTeam] = useState(localStorage.getItem("team") ?? "");
  const [selectedBlock, setSelectedBlock] = useState();
  const [selectedBlockForModal, setSelectedBlockForModal] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openBlockModal = (block) => {
    setSelectedBlockForModal(block);
    setIsModalOpen(true);
  };

  const [blocks, setBlocks] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [pendingTransactions, setPendingTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("hash");
  const [redParticipantCount, setRedParticipantCount] = useState(0);
  const [blueParticipantCount, setBlueParticipantCount] = useState(0);
  const [isMiningDisabled, setIsMiningDisabled] = useState(false);
  const [miningTimer, setMiningTimer] = useState(0);
  const { user, userData } = UserAuth();
  const transactionCollection = team === "red" ? "redTeamTransactions" : "blueTeamTransactions";
  const blocksCollections = team === "red" ? "redTeamBlocks" : "blueTeamBlocks";

  useEffect(() => {
    if (!roomId) return;
    const unsubscribe = db
      .collection("rooms")
      .doc(roomId)
      .onSnapshot((doc) => {
        if (doc.exists) {
          const data = doc.data();
          setRedParticipantCount((data.red_participants || []).length);
          setBlueParticipantCount((data.blue_participants || []).length);
        }
      });

    return () => unsubscribe();
  }, [roomId]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (e) => {
    setFilter(e.target.value);
  };

  useEffect(() => {
    if (!roomId) return;
    const roomRef = doc(db_modular, "rooms", roomId);

    const unsubscribe = onSnapshot(collection(roomRef, blocksCollections), (snapshot) => {
      const tempBlocks = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      setBlocks(tempBlocks);
    });

    return () => unsubscribe();
  }, [roomId, team]);

  useEffect(() => {
    if (!roomId) return;
    const roomRef = doc(db_modular, "rooms", roomId);

    const unsubscribe = onSnapshot(collection(roomRef, transactionCollection), (snapshot) => {
      setAllTransactions(snapshot.docs.map((v) => ({ id: v.id, ...v.data() })));
    });

    return () => unsubscribe();
  }, [roomId, team]);

  useEffect(() => {
    const minedTransactionIds = new Set();
    blocks.forEach((b) => {
      if (b.transactions) {
        b.transactions.forEach((t) => minedTransactionIds.add(t.id));
      }
    });

    const filtered = allTransactions.filter(
      (t) => t.status === "Added to block" && !minedTransactionIds.has(t.id)
    );

    if (filtered.length === 0) {
      setPendingTransactions([]);
      return;
    }

    // Group transactions into chunks of 3-5
    const sorted = [...filtered].sort(
      (a, b) => (a.addedAt?.toMillis() || 0) - (b.addedAt?.toMillis() || 0)
    );

    const chunks = [];
    for (let i = 0; i < sorted.length; i += 5) {
      const chunk = sorted.slice(i, i + 5);
      if (chunk.length >= 3) {
        chunks.push({
          minTS: chunk[0].addedAt?.toMillis() || 0,
          timestamp: Math.floor((chunk[0].addedAt?.toMillis() || Date.now()) / 1000),
          transactions: chunk,
        });
      }
    }

    setPendingTransactions(chunks);
  }, [blocks, allTransactions]);

  async function saveBlock(calculatedHash, timestamp, previousBlockHash, transactions, nonce) {
    const MINING_COST = 5;
    const MINING_REWARD = 25;
    try {
      const reward = transactions.reduce((acc, curr) => {
        return acc + curr.amount * 0.1;
      }, 0);

      const otherTeamBlocksCollection = team === "red" ? "blueTeamBlocks" : "redTeamBlocks";
      const otherTeamBlocksRes = await getDocs(
        query(collection(db_modular, "rooms", roomId, otherTeamBlocksCollection))
      );
      const otherChainLength = otherTeamBlocksRes.size;
      const myChainLength = blocks.length;

      const isLongestChain = myChainLength + 1 >= otherChainLength;
      const finalReward = isLongestChain ? reward + MINING_REWARD : reward;

      if (userData.balance < MINING_COST) {
        toast.error("Insufficient balance to mine block");
        return;
      }
      const receivers = Object.fromEntries(
        (
          await getDocs(
            query(
              collection(db_modular, "users"),
              where(
                "publicKey",
                "in",
                transactions.map((t) => t.receiver)
              )
            )
          )
        ).docs.map((user) => [user.data().publicKey, { id: user.id, ...user.data() }])
      );

      if (team === "red") {
        await runTransaction(db_modular, async (dbTransaction) => {
          const roomDocRef = doc(db_modular, "rooms", roomId);
          const roomDoc = await dbTransaction.get(roomDocRef);

          if (!roomDoc.exists()) {
            throw new Error("Room does not exist!");
          }

          const roomData = roomDoc.data();
          const nextIndex = blocks.length;

          // Add Coinbase Transactions (+25 reward, -5 cost)
          const coinbaseTxs = [
            {
              id: `cb-reward-${calculatedHash}`,
              sender: "SYSTEM",
              senderEmail: "blockchain@system",
              receiver: userData.publicKey,
              amount: MINING_REWARD,
              status: "Coinbase Reward",
              hash: `cb-reward-${calculatedHash}`,
              timestamp: dayjs().unix(),
            },
            {
              id: `cb-cost-${calculatedHash}`,
              sender: userData.publicKey,
              senderEmail: user.email,
              receiver: "SYSTEM",
              amount: MINING_COST,
              status: "Mining Cost",
              hash: `cb-cost-${calculatedHash}`,
              timestamp: dayjs().unix(),
            },
          ];

          const finalTransactions = [...coinbaseTxs, ...transactions];

          dbTransaction.set(doc(db_modular, "rooms", roomId, "redTeamBlocks", calculatedHash), {
            timestamp,
            previousBlockHash,
            hash: calculatedHash,
            transactions: finalTransactions,
            nonce,
            index: nextIndex,
            created_at: serverTimestamp(),
          });

          dbTransaction.update(roomDocRef, {
            "blockCounts.red": increment(1),
          });

          dbTransaction.update(doc(db_modular, "users", user.email), {
            balance: increment(finalReward - MINING_COST),
          });

          transactions.forEach((t) => {
            receivers.hasOwnProperty(t.receiver) &&
              dbTransaction.update(doc(db_modular, "users", receivers[t.receiver].email), {
                balance: increment(t.amount * 0.9),
              });

            dbTransaction.update(doc(db_modular, "rooms", roomId, "redTeamTransactions", t.id), {
              status: "Added to chain",
            });
          });
        });

        toast.success("Added block to blockchain");
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        await runTransaction(db_modular, async (dbTransaction) => {
          const roomDocRef = doc(db_modular, "rooms", roomId);
          const roomDoc = await dbTransaction.get(roomDocRef);

          if (!roomDoc.exists()) {
            throw new Error("Room does not exist!");
          }

          const roomData = roomDoc.data();
          const nextIndex = blocks.length;

          // Add Coinbase Transactions (+25 reward, -5 cost)
          const coinbaseTxs = [
            {
              id: `cb-reward-${calculatedHash}`,
              sender: "SYSTEM",
              senderEmail: "blockchain@system",
              receiver: userData.publicKey,
              amount: MINING_REWARD,
              status: "Coinbase Reward",
              hash: `cb-reward-${calculatedHash}`,
              timestamp: dayjs().unix(),
            },
            {
              id: `cb-cost-${calculatedHash}`,
              sender: userData.publicKey,
              senderEmail: user.email,
              receiver: "SYSTEM",
              amount: MINING_COST,
              status: "Mining Cost",
              hash: `cb-cost-${calculatedHash}`,
              timestamp: dayjs().unix(),
            },
          ];

          const finalTransactions = [...coinbaseTxs, ...transactions];

          dbTransaction.set(doc(db_modular, "rooms", roomId, "blueTeamBlocks", calculatedHash), {
            timestamp,
            previousBlockHash,
            hash: calculatedHash,
            transactions: finalTransactions,
            nonce,
            index: nextIndex,
            created_at: serverTimestamp(),
          });

          dbTransaction.update(roomDocRef, {
            "blockCounts.blue": increment(1),
          });

          dbTransaction.update(doc(db_modular, "users", user.email), {
            balance: increment(finalReward - MINING_COST),
          });

          transactions.forEach((t) => {
            receivers.hasOwnProperty(t.receiver) &&
              dbTransaction.update(doc(db_modular, "users", receivers[t.receiver].email), {
                balance: increment(t.amount * 0.9),
              });

            dbTransaction.update(doc(db_modular, "rooms", roomId, "blueTeamTransactions", t.id), {
              status: "Added to chain",
            });
          });
        });

        toast.success("Added block to blockchain");
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (e) {
      console.error("Failed to save block", e);
      toast.error("Failed to save block");
    }
  }

  const renderTrimmedAddress = (address) => `${address.substring(0, 10)}...`;

  const copyToClipboard = (e, data) => {
    e.preventDefault();

    navigator.clipboard.writeText(data).then(
      () => {
        toast.success("Copied Successfully");
      },
      (err) => {
        toast.error("Failed to copy");
      }
    );
  };

  const isRedTeam = team === "red";
  const teamColor = isRedTeam ? "from-red-500 to-red-600" : "from-blue-500 to-blue-600";
  const teamAccent = isRedTeam ? "text-red-400" : "text-blue-400";
  const teamBorder = isRedTeam ? "border-red-500/30" : "border-blue-500/30";

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="w-full">
        {/* Header */}
        <div className={`glass-card p-6 mb-6 border ${teamBorder}`}>
          <h2 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            <span
              className={`px-3 py-1 rounded-lg bg-gradient-to-r ${teamColor} text-white text-sm font-medium`}
            >
              {team.charAt(0).toUpperCase() + team.slice(1)} Team
            </span>
            <FiBox className={teamAccent} />
            Blocks
            <span className="text-text-muted font-normal text-lg">Room: {roomId}</span>
          </h2>
        </div>

        {/* Search & Filter */}
        <div className={`glass-card p-4 mb-6 border ${teamBorder}`}>
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="relative flex-1 w-full">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search blocks..."
                className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-3 text-white placeholder-text-muted focus:outline-none focus:border-primary transition-colors"
              />
            </div>
            <div className="relative w-full sm:w-auto">
              <FiFilter className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
              <select
                value={filter}
                onChange={handleFilterChange}
                className="w-full sm:w-48 bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-3 text-white appearance-none focus:outline-none focus:border-primary transition-colors cursor-pointer"
              >
                <option value="hash" className="bg-surface">
                  Transaction Hash
                </option>
                <option value="nonce" className="bg-surface">
                  Nonce
                </option>
                <option value="sender" className="bg-surface">
                  Sender
                </option>
                <option value="receiver" className="bg-surface">
                  Receiver
                </option>
                <option value="signature" className="bg-surface">
                  Signature
                </option>
                <option value="status" className="bg-surface">
                  Status
                </option>
                <option value="timestamp" className="bg-surface">
                  Timestamp
                </option>
                <option value="amount" className="bg-surface">
                  Amount
                </option>
              </select>
            </div>
          </div>
        </div>

        {/* Blocks Table */}
        <div className={`glass-card p-6 mb-6 overflow-x-auto border ${teamBorder}`}>
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="py-4 px-4 text-left text-text-muted text-sm font-medium uppercase tracking-wider">
                  Block
                </th>
                <th className="py-4 px-4 text-left text-text-muted text-sm font-medium uppercase tracking-wider">
                  Transactions
                </th>
                <th className="py-4 px-4 text-left text-text-muted text-sm font-medium uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {[...pendingTransactions, ...blocks].map((block, index) => {
                const allItems = [...pendingTransactions, ...blocks];
                const blockNumber = allItems.length - 1 - index;
                const uniqueKey = block.hash || `pending-${block.timestamp}-${index}`;
                return (
                  <tr
                    key={uniqueKey}
                    className={clsx(
                      "border-b border-white/5 transition-colors",
                      block.hash ? "hover:bg-white/5 cursor-pointer" : "hover:bg-white/2"
                    )}
                    onClick={() => block.hash && openBlockModal(block)}
                  >
                    {!block.hash ? (
                      <>
                        <td className="py-4 px-4 align-top">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-500/10 text-yellow-500 border border-yellow-500/20 whitespace-nowrap">
                            Block #{blockNumber} (Pending)
                          </span>
                        </td>

                        <td className="py-4 px-4">
                          <div className="overflow-hidden rounded-xl border border-white/5 bg-black/20">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-white/5 border-b border-white/5">
                                  <th className="py-2 px-3 text-left text-text-muted text-xs font-semibold">
                                    Hash
                                  </th>
                                  <th className="py-2 px-3 text-left text-text-muted text-xs font-semibold">
                                    Time
                                  </th>
                                  <th className="py-2 px-3 text-left text-text-muted text-xs font-semibold">
                                    Sender
                                  </th>
                                  <th className="py-2 px-3 text-left text-text-muted text-xs font-semibold">
                                    Receiver
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-white/5">
                                {block.transactions &&
                                  block.transactions.length > 0 &&
                                  block.transactions.map((t, bTxIdx) => (
                                    <tr
                                      key={t.id || `pending-block-${index}-${bTxIdx}`}
                                      className="hover:bg-white/5 transition-colors"
                                    >
                                      <td className="py-2 px-3">
                                        <code
                                          className="text-primary bg-primary/10 px-1.5 py-0.5 rounded text-xs truncate max-w-[80px] block"
                                          title={t.hash}
                                        >
                                          {t.hash}
                                        </code>
                                      </td>
                                      <td className="py-2 px-3 text-text-muted text-xs whitespace-nowrap">
                                        {t.addedAt && t.addedAt.toDate().toLocaleTimeString()}
                                      </td>
                                      <td className="py-2 px-3">
                                        <code
                                          className="text-emerald-400 bg-emerald-400/10 px-1.5 py-0.5 rounded text-xs truncate max-w-[80px] block"
                                          title={t.sender}
                                        >
                                          {t.sender}
                                        </code>
                                      </td>
                                      <td className="py-2 px-3">
                                        <code
                                          className="text-blue-400 bg-blue-400/10 px-1.5 py-0.5 rounded text-xs truncate max-w-[80px] block"
                                          title={t.receiver}
                                        >
                                          {t.receiver}
                                        </code>
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                        <td className="py-4 px-4 align-top text-right">
                          {block.status !== "genesis" && !block.id && (
                            <button
                              className="btn-primary text-xs py-1.5 px-3 inline-flex items-center gap-2 whitespace-nowrap ml-auto"
                              onClick={() => {
                                const nextBlock = allItems[index + 1];
                                // Use specific genesis hash if no previous block, or all zeros
                                const previousBlockHash = nextBlock
                                  ? nextBlock.hash
                                  : "0".repeat(64);

                                setSelectedBlock({
                                  transactions: (block.transactions && block.transactions) || [],
                                  previousBlockHash: previousBlockHash,
                                });
                              }}
                            >
                              Add Block #{blockNumber}
                            </button>
                          )}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-4 px-4 align-top">
                          <code
                            className="text-white bg-white/10 px-2 py-1 rounded text-sm block truncate max-w-[150px] mb-1"
                            title={block.hash}
                          >
                            {block.hash}
                          </code>
                          <span className="text-[10px] text-text-muted font-medium ml-1">
                            {block.timestamp
                              ? typeof block.timestamp === "number"
                                ? dayjs.unix(block.timestamp).format("MMM DD, YYYY HH:mm:ss")
                                : dayjs(block.timestamp).format("MMM DD, YYYY HH:mm:ss")
                              : "N/A"}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          {block.status === "genesis" ? (
                            <span className="text-text-muted italic">Genesis Block</span>
                          ) : (
                            <div className="overflow-hidden rounded-xl border border-white/5 bg-black/20">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="bg-white/5 border-b border-white/5">
                                    <th className="py-2 px-3 text-left text-text-muted text-xs font-semibold">
                                      Hash
                                    </th>
                                    <th className="py-2 px-3 text-left text-text-muted text-xs font-semibold">
                                      Time
                                    </th>
                                    <th className="py-2 px-3 text-left text-text-muted text-xs font-semibold">
                                      Sender
                                    </th>
                                    <th className="py-2 px-3 text-left text-text-muted text-xs font-semibold">
                                      Receiver
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                  {block.transactions &&
                                    block.transactions.length > 0 &&
                                    block.transactions.map((t, txIdx) => (
                                      <tr
                                        key={t.id || `block-tx-${index}-${txIdx}`}
                                        className="hover:bg-white/5 transition-colors"
                                      >
                                        <td className="py-2 px-3">
                                          <code
                                            className="text-primary bg-primary/10 px-1.5 py-0.5 rounded text-xs truncate max-w-[80px] block"
                                            title={t.hash}
                                          >
                                            {t.hash}
                                          </code>
                                        </td>
                                        <td className="py-2 px-3 text-text-muted text-xs whitespace-nowrap">
                                          {t.timestamp
                                            ? dayjs.unix(t.timestamp).format("HH:mm:ss")
                                            : t.addedAt?.toDate().toLocaleTimeString() || "N/A"}
                                        </td>
                                        <td className="py-2 px-3">
                                          <code
                                            className="text-emerald-400 bg-emerald-400/10 px-1.5 py-0.5 rounded text-xs truncate max-w-[80px] block"
                                            title={t.sender}
                                          >
                                            {t.sender}
                                          </code>
                                        </td>
                                        <td className="py-2 px-3">
                                          <code
                                            className="text-blue-400 bg-blue-400/10 px-1.5 py-0.5 rounded text-xs truncate max-w-[80px] block"
                                            title={t.receiver}
                                          >
                                            {t.receiver}
                                          </code>
                                        </td>
                                      </tr>
                                    ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </td>
                        <td className="py-4 px-4 align-top text-right text-text-muted text-xs">
                          Block #{blockNumber}
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mining Visualizer */}
        <div className={`glass-card p-6 border ${teamBorder}`}>
          <h2 className="text-xl font-bold text-white text-center mb-6 flex items-center justify-center gap-2">
            <FiCpu className={teamAccent} /> Mining Visualizer
          </h2>
          <Visualizer
            transactions={(selectedBlock && selectedBlock.transactions) || []}
            previousBlockHash={(selectedBlock && selectedBlock.previousBlockHash) || undefined}
            save={saveBlock}
            teamAccent={teamAccent}
            team={team}
            redParticipantCount={redParticipantCount}
            blueParticipantCount={blueParticipantCount}
            isMiningDisabled={isMiningDisabled}
            setIsMiningDisabled={setIsMiningDisabled}
            miningTimer={miningTimer}
            setMiningTimer={setMiningTimer}
          />
        </div>
      </div>

      <BlockTransactionsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        block={selectedBlockForModal}
        team={team}
      />
    </div>
  );
}

function Visualizer({
  transactions = [],
  previousBlockHash,
  initialNonce = 0,
  timestamp = dayjs().unix(),
  difficulty = 2,
  save,
  teamAccent,
  team,
  redParticipantCount,
  blueParticipantCount,
  isMiningDisabled,
  setIsMiningDisabled,
  miningTimer,
  setMiningTimer,
}) {
  const [nonce, setNonce] = useState(initialNonce ?? 0);
  const [calculatedHash, setCalculatedHash] = useState("");
  const [foundHash, setFoundHash] = useState(false);
  const [roomId, setRoomId] = useState(localStorage.getItem("roomId") ?? "");

  async function mine(hash = calculateHash(transactions, nonce, timestamp, previousBlockHash)) {
    if (foundHash || isMiningDisabled) return;

    const totalPlayers = redParticipantCount + blueParticipantCount;
    const myTeamPlayers = team === "red" ? redParticipantCount : blueParticipantCount;
    const waitTime = Math.ceil(5 * (totalPlayers / (myTeamPlayers || 1)));

    setIsMiningDisabled(true);
    setFoundHash(false); // Reset found status if starting over
    setMiningTimer(waitTime);

    toast.loading(`Mining resources allocated. Please wait ${waitTime}s...`, {
      id: "mining-toast",
      duration: waitTime * 1000,
    });

    const timerInterval = setInterval(() => {
      setMiningTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerInterval);
          setIsMiningDisabled(false);
          toast.success("Mining resources ready!", { id: "mining-toast" });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    const start = Date.now();

    let nonce = initialNonce;

    while (hash.slice(0, difficulty) !== "0".repeat(difficulty) && !foundHash) {
      const current = Date.now();

      hash = calculateHash(transactions, nonce, timestamp, previousBlockHash);

      setCalculatedHash(hash);

      const currentTime = Math.floor((current - start) / 1000);

      setNonce(++nonce);

      await new Promise((res) => setTimeout(res, 5));
    }

    setFoundHash(true);
    toast.success("Block hash successfully calculated!");

    const end = Date.now();
    const time = (end - start) / 1000;
  }

  return (
    <div>
      {transactions && transactions.length < 1 && (
        <div className="text-center text-text-muted py-8">Select Transactions to Mine a Block</div>
      )}
      {transactions && transactions.length > 0 && (
        <div className="glass-card p-6 max-w-2xl mx-auto border border-primary/30">
          <div className="text-center border-b border-white/10 pb-4 mb-4">
            <h3 className="text-xl font-semibold text-white flex items-center justify-center gap-2">
              <FiCpu className="text-primary" /> Block Miner
            </h3>
          </div>
          <div className="space-y-4">
            <div className="flex flex-col gap-2">
              <label className="text-text-muted text-sm">Nonce:</label>
              <div className="bg-primary/20 text-primary px-4 py-2 rounded-lg font-mono">
                {nonce}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-text-muted text-sm">Calculated Hash:</label>
              <div className="bg-white/10 text-white px-4 py-2 rounded-lg font-mono text-sm overflow-x-auto whitespace-nowrap custom-scrollbar">
                {calculatedHash || "Not calculated yet"}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-text-muted text-sm">Status:</label>
              <div
                className={`px-4 py-2 rounded-lg font-medium ${
                  foundHash ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"
                }`}
              >
                {foundHash ? "Hash Found!" : "Waiting to Mine"}
              </div>
            </div>
          </div>
          <div className="flex gap-4 items-center justify-center mt-6">
            <button
              className={clsx(
                "btn-primary flex items-center gap-2",
                isMiningDisabled && "opacity-50 cursor-not-allowed"
              )}
              onClick={() => mine()}
              disabled={isMiningDisabled}
            >
              {isMiningDisabled ? `Wait ${miningTimer}s` : "Mine"}
            </button>
            {foundHash && (
              <button
                className="px-6 py-3 rounded-xl font-semibold text-white bg-green-500 hover:bg-green-600 transition-all flex items-center gap-2"
                onClick={() =>
                  save(calculatedHash, timestamp, previousBlockHash, transactions, nonce)
                }
              >
                Save to BlockChain
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
